import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdBanner({ position, className = '' }) {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    axios.get(`${API}/ads/active?position=${position}`)
      .then(res => {
        setAds(res.data.ads || []);
        // Track impression for all loaded ads
        (res.data.ads || []).forEach(ad => {
          axios.post(`${API}/ads/${ad.id}/impression`).catch(() => {});
        });
      })
      .catch(() => {});
  }, [position]);

  // Rotate ads every 8 seconds if multiple
  useEffect(() => {
    if (ads.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(i => (i + 1) % ads.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [ads.length]);

  const handleClick = useCallback(async (ad) => {
    try {
      const res = await axios.post(`${API}/ads/${ad.id}/click`);
      if (res.data.link_url) {
        window.open(res.data.link_url, '_blank', 'noopener');
      }
    } catch {}
  }, []);

  if (ads.length === 0) return null;

  const ad = ads[currentIndex];
  const isCompact = position === 'search_sidebar' || position === 'detail_sidebar';

  return (
    <div className={`relative group ${className}`} data-testid={`ad-banner-${position}`}>
      <div
        onClick={() => handleClick(ad)}
        className={`cursor-pointer rounded-xl overflow-hidden border border-border/60 transition-all hover:shadow-md ${
          isCompact ? 'p-4' : 'p-5'
        } bg-gradient-to-br from-slate-50 to-white`}
      >
        {ad.image_url ? (
          <img
            src={ad.image_url.startsWith('http') ? ad.image_url : `${API}/files/${ad.image_url}`}
            alt={ad.title}
            className="w-full rounded-lg object-cover mb-3"
            style={{ maxHeight: isCompact ? '120px' : '180px' }}
          />
        ) : (
          <div className={`w-full rounded-lg bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center mb-3 ${isCompact ? 'h-24' : 'h-36'}`}>
            <div className="text-center px-4">
              <p className="font-heading font-bold text-primary text-sm leading-snug">{ad.title}</p>
              {ad.business_name && <p className="text-xs text-accent mt-1 font-medium">{ad.business_name}</p>}
            </div>
          </div>
        )}
        <div>
          <p className={`font-heading font-semibold text-slate-800 ${isCompact ? 'text-xs' : 'text-sm'}`}>{ad.title}</p>
          {ad.business_name && <p className="text-xs text-slate-500 mt-0.5">{ad.business_name}</p>}
        </div>
        {/* Sponsored label */}
        <div className="absolute top-2 right-2">
          <span className="text-[10px] text-slate-400 bg-white/80 backdrop-blur-sm px-1.5 py-0.5 rounded">Ad</span>
        </div>
      </div>
      {/* Dots for multiple ads */}
      {ads.length > 1 && (
        <div className="flex justify-center gap-1 mt-2">
          {ads.map((_, i) => (
            <button key={i} onClick={() => setCurrentIndex(i)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIndex ? 'bg-primary' : 'bg-slate-300'}`} />
          ))}
        </div>
      )}
    </div>
  );
}
